import { useEffect } from "react";

/**
 * Publica a **medida** de uma barra flutuante como custom property na raiz.
 *
 * **Por que medir em vez de escrever o número.** A barra de ação é
 * `position: sticky; bottom: 0`: ela é pintada por cima do que rola por baixo,
 * e o conteúdo do fim do fluxo precisa de um respiro do tamanho dela. Só que
 * esse tamanho muda — a etiqueta quebra em duas linhas no estreito, a
 * `env(safe-area-inset-bottom)` entra em aparelho com barra de gestos, e a
 * navegação inferior existe abaixo de `--quebra-largo` e some acima. Um número
 * escrito no CSS acerta uma largura e erra as outras: foi assim que a quinta
 * alternativa da tela de execução ficou atrás da barra em 390 **e** em 1440, e
 * que a pílula "Salvo" pousou no meio do texto da nota.
 *
 * O `--alvo-minimo` e os espaçamentos continuam vindo de token; o que vem daqui
 * é só o que **não** dá para saber sem olhar a tela pintada.
 *
 * **Por seletor, e montado uma vez só.** A barra aparece em dez telas e a
 * navegação troca de forma com a largura. Fazer cada tela publicar a própria
 * medida significaria dez pontos para esquecer um. Aqui a raiz observa quem
 * estiver no documento agora, e volta a `0px` quando não há ninguém — o `calc`
 * do CSS continua válido nos dois casos.
 *
 * **Só altura.** Houve aqui um eixo de largura, e ele existia por um motivo só:
 * o alvo flutuante do caderno ficava no canto inferior **esquerdo** no largo, e
 * precisava descontar a barra lateral. O menu do rodapé foi para a direita nas
 * duas larguras (RN-42, ADR 028), o canto deixou de ser da lateral, e medir a
 * largura dela virou observador rodando para ninguém ler.
 */
export function useAlturaPublicada(seletor: string, propriedade: string): void {
  useEffect(() => {
    const raiz = document.documentElement;
    let observado: Element | null = null;

    const publicar = (medida: number) => {
      raiz.style.setProperty(propriedade, `${Math.round(medida)}px`);
    };

    const observadorDeTamanho = new ResizeObserver((entradas) => {
      const e = entradas[0];
      if (!e) return;
      publicar(e.contentRect.height + bordasEPadding(e.target));
    });

    const reavaliar = () => {
      const atual = document.querySelector(seletor);
      if (atual === observado) return;
      if (observado) observadorDeTamanho.unobserve(observado);
      observado = atual;
      if (atual) {
        observadorDeTamanho.observe(atual);
        publicar(atual.getBoundingClientRect().height);
      } else {
        publicar(0);
      }
    };

    // A barra entra e sai do documento com a troca de rota, e nenhum evento do
    // React alcança isto de fora da árvore da tela.
    const observadorDeArvore = new MutationObserver(reavaliar);
    observadorDeArvore.observe(document.body, { childList: true, subtree: true });
    reavaliar();

    return () => {
      observadorDeArvore.disconnect();
      observadorDeTamanho.disconnect();
      raiz.style.removeProperty(propriedade);
    };
  }, [seletor, propriedade]);
}

/**
 * `contentRect` do `ResizeObserver` é a caixa de conteúdo; a barra tem
 * `padding` — inclusive o da área segura. Sem somar isso de volta, o respiro
 * reservado fica menor que a barra justamente no aparelho com barra de gestos,
 * que é onde o defeito dói.
 */
function bordasEPadding(alvo: Element): number {
  const cs = getComputedStyle(alvo);
  return (
    parseFloat(cs.paddingBlockStart || "0") +
    parseFloat(cs.paddingBlockEnd || "0") +
    parseFloat(cs.borderBlockStartWidth || "0") +
    parseFloat(cs.borderBlockEndWidth || "0")
  );
}
